import { notifications } from '@mantine/notifications';
import { EntityCategorizationService } from '../services/EntityCategorizationService';
import { pushHashRoute } from './hashNavigationUtils';
import { copyToClipboard } from './clipboardUtils';

/**
 * Build in-app hash for viewing an entity by type and id.
 * Schema meta types use table deep-link shape (#meta/schemaattribute/{id}); admin types use /view/.
 */
export function buildEntityViewHash(entityType: string, entityId: string): string {
    const type = (entityType ?? '').trim().toLowerCase();
    const id = encodeURIComponent((entityId ?? '').trim());
    if (!type || !id) return '#';

    if (type === 'schemaattribute') {
        return `#meta/schemaattribute/${id}`;
    }
    if (type === 'schemaentity') {
        return `#meta/schemaentity/${id}`;
    }
    if (type === 'schemabinding') {
        return `#meta/schemabinding/${id}`;
    }
    if (type === 'group') {
        return `#groups/view/${id}`;
    }
    if (type === 'class') {
        return `#classes/view/${id}`;
    }

    return `#admin/${type}/view/${id}`;
}

/**
 * Resolve view hash using entity categorization (person, locality, admin, other).
 * Use for reference badges/links when type alone is not enough (e.g. contractor → #Person/...).
 */
export async function resolveEntityViewHash(entityType: string, entityId: string): Promise<string> {
    const type = (entityType ?? '').trim().toLowerCase();
    const id = encodeURIComponent((entityId ?? '').trim());
    if (!type || !id) return '#';

    if (type === 'schemaattribute') return `#meta/schemaattribute/${id}`;
    if (type === 'schemaentity') return `#meta/schemaentity/${id}`;
    if (type === 'schemabinding') return `#meta/schemabinding/${id}`;
    if (type === 'group') return `#groups/view/${id}`;
    if (type === 'class') return `#classes/view/${id}`;

    try {
        const category = await EntityCategorizationService.getInstance().getEntityCategory(type);
        if (category.type === 'person') return `#Person/${type}/view/${id}`;
        if (category.type === 'locality') return `#localities/${type}/view/${id}`;
        if (category.type === 'admin') return `#admin/${type}/view/${id}`;
        return `#other/${type}/view/${id}`;
    } catch {
        if (
            type === 'set' ||
            type === 'policyrule' ||
            type === 'journalentry' ||
            type === 'modificationerror' ||
            type === 'functioncatalog'
        ) {
            const path = type === 'functioncatalog' ? 'functionCatalog' : type;
            return `#admin/${path}/view/${id}`;
        }
        return buildEntityViewHash(entityType, entityId);
    }
}

export function buildEntityViewFullUrl(entityType: string, entityId: string): string {
    const hash = buildEntityViewHash(entityType, entityId);
    if (typeof window === 'undefined') return hash;
    return `${window.location.origin}${window.location.pathname}${window.location.search || ''}${hash}`;
}

export function openEntityViewSameTab(entityType: string, entityId: string): void {
    pushHashRoute(buildEntityViewHash(entityType, entityId));
}

export function openEntityViewNewTab(entityType: string, entityId: string): void {
    window.open(buildEntityViewFullUrl(entityType, entityId), '_blank', 'noopener,noreferrer');
}

export async function copyEntityViewLinkWithToast(entityType: string, entityId: string): Promise<void> {
    const url = buildEntityViewFullUrl(entityType, entityId);
    const ok = await copyToClipboard(url);
    if (ok) {
        notifications.show({
            color: 'green',
            message: `Copied link ${url}`,
        });
    } else {
        notifications.show({
            color: 'red',
            message: 'Could not copy link to clipboard.',
        });
    }
}
